import { useState, useCallback, useMemo } from "react";
import moment from "moment-timezone";
import type {
  Action,
  CalendarAddDisplayOptions,
  SelectableOption,
} from "@core/features/actions";
import {
  useFetchActions,
  useUpdateActionStatus,
  ACTION_STATUS,
  ACTION_TYPE_SLUG,
} from "@core/features/actions";
import {
  useGetSchedulingAvailability,
  getSelectedRepeatingOptionsFromSlots,
  type ProcessedSelectableOption,
} from "@core/features/scheduling";
import { isCircleVisible, useFetchUserCircles } from "@core/features/circle";
import { useUserMeetings } from "@platform/hooks/useUserMeetings";
import { useCurrentMeeting } from "@platform/hooks/useCurrentMeeting";
import { useUserTimezoneOption } from "@platform/hooks/useUserTimezoneOption";
import { useProgram } from "@platform/hooks/useProgram";
import { usePlatformStore } from "@platform/store";
import { ProgramCalendarEventList } from "./ProgramCalendarEventList";

/** Number of weekly meetings generated from a visible circle. */
const COHORT_MEETING_WEEKS = 5;

/** Convert a weekly slot id (0-335) to its UTC day-of-week, hour, and minute. */
function slotIdToUtcTime(slotId: number) {
  const dayIndex = Math.floor(slotId / 48);
  const remainder = slotId % 48;
  const hour = Math.floor(remainder / 2);
  const minute = remainder % 2 === 0 ? 0 : 30;
  return { dayIndex, hour, minute };
}

export type Props = {
  /** The calendar_add action */
  calendarAddAction: Action;
  /** When true, render only the accordion meetings list (suppress selectableOptions). */
  meetingsOnly?: boolean;
};

/** Action type slugs that are bound to meetings */
const MEETING_BOUND_SLUGS = new Set([
  ACTION_TYPE_SLUG.MEETING_SURVEY,
  ACTION_TYPE_SLUG.MEETING_READING,
  ACTION_TYPE_SLUG.MEETING_SCRIPT,
]);

/**
 * Process selectableOptions: generate IDs and compute day from date
 */
function processSelectableOptions(selectableOptions: SelectableOption[]) {
  return selectableOptions.map((opt, index) => ({
    ...opt,
    id: opt.id || `opt-${opt.date}-${opt.startHour}-${opt.startMinute}-${index}`,
    day: opt.day ?? moment.utc(opt.date).day(),
  }));
}

/**
 * Container for ProgramCalendarEventList.
 * Handles data fetching and determines whether to show meetings or scheduled times.
 */
export function ProgramCalendarEventListContainer({
  calendarAddAction,
  meetingsOnly = false,
}: Props) {
  const user = usePlatformStore((s) => s.user);
  const isVideoDisabled = usePlatformStore((s) => s.featureFlags.isVideoDisabled);
  const [expandedMeetingId, setExpandedMeetingId] = useState<number | null>(null);
  const [hasUserToggled, setHasUserToggled] = useState(false);

  // Get user's meetings and current active meeting
  const { meetings } = useUserMeetings();
  const { currentMeeting } = useCurrentMeeting();

  // Get user's timezone
  const userTimezoneOption = useUserTimezoneOption();

  // Fetch actions for this program to find meeting-bound actions
  const { data: actionsData } = useFetchActions({
    programId: calendarAddAction.program,
    user,
    isEnabled: !!user,
  });

  // Group meeting-bound actions by binding_object_id (meeting ID)
  // Also extract MEETING actions as the data source for each meeting card
  const { actionsByMeetingId, meetingActionByMeetingId } = useMemo(() => {
    if (!actionsData?.actions) return { actionsByMeetingId: {}, meetingActionByMeetingId: {} };
    const grouped: Record<number, Action[]> = {};
    const meetingActions: Record<number, Action> = {};

    for (const action of actionsData.actions) {
      const slug = action.action_type?.slug;
      if (!slug) continue;

      const meetingId = action.binding_object_id;
      if (!meetingId) continue;

      if (slug === ACTION_TYPE_SLUG.MEETING) {
        meetingActions[meetingId] = action;
      } else if (MEETING_BOUND_SLUGS.has(slug)) {
        if (!grouped[meetingId]) grouped[meetingId] = [];
        grouped[meetingId].push(action);
      }
    }

    return { actionsByMeetingId: grouped, meetingActionByMeetingId: meetingActions };
  }, [actionsData?.actions]);

  // Extract selectableOptions, eventTitle, and eventDescription from action's display_options
  const { selectableOptions, eventTitle, eventDescription } = useMemo(() => {
    const displayOptions =
      calendarAddAction.display_options as CalendarAddDisplayOptions | null;

    return {
      selectableOptions: displayOptions?.selectableOptions
        ? processSelectableOptions(displayOptions.selectableOptions)
        : undefined,
      eventTitle:
        displayOptions?.eventTitle ||
        calendarAddAction.name ||
        "Peer Circle Meeting",
      eventDescription: displayOptions?.eventDescription,
    };
  }, [calendarAddAction]);

  // Fetch user's saved scheduling availability
  const { data: savedTimeSlots } = useGetSchedulingAvailability(
    calendarAddAction.program
  );

  // Once the user is matched to a visible cohort, the calendar reflects the
  // circle's actual meeting slot rather than the user's saved availability.
  // We generate a fixed number of weekly meetings anchored to the program's
  // engagement start, using the circle's start/end slots as the source of
  // truth for time and duration.
  const { data: circlesData } = useFetchUserCircles({ user });
  const visibleCircle = useMemo(
    () => (circlesData?.circles ?? []).find(isCircleVisible) ?? null,
    [circlesData?.circles]
  );

  const { program } = useProgram();
  const scheduledEngagementStartDate = program?.scheduled_engagement_start_date;

  const circleDerivedOptions = useMemo<
    ProcessedSelectableOption[] | undefined
  >(() => {
    if (
      !visibleCircle ||
      !scheduledEngagementStartDate ||
      typeof visibleCircle.start_time_slot !== "number" ||
      typeof visibleCircle.end_time_slot !== "number"
    ) {
      return undefined;
    }

    const start = slotIdToUtcTime(visibleCircle.start_time_slot);
    const end = slotIdToUtcTime(visibleCircle.end_time_slot);
    const duration =
      end.hour + end.minute / 60 - (start.hour + start.minute / 60);

    const weekStart = moment.utc(scheduledEngagementStartDate).startOf("week");
    const options: ProcessedSelectableOption[] = [];

    for (let week = 0; week < COHORT_MEETING_WEEKS; week++) {
      const date = weekStart
        .clone()
        .add(week * 7 + start.dayIndex, "days")
        .format("YYYY-MM-DD");
      options.push({
        id: `circle-${visibleCircle.id}-${date}`,
        day: start.dayIndex,
        date,
        startHour: start.hour,
        startMinute: start.minute,
        endHour: end.hour,
        endMinute: end.minute,
        duration,
      });
    }
    return options;
  }, [visibleCircle, scheduledEngagementStartDate]);

  // Determine which selectableOptions to display:
  // - If the user is in a visible cohort, use the circle-derived options.
  // - Otherwise filter the action's options by the user's saved slots.
  const selectedOptions = useMemo(() => {
    if (circleDerivedOptions?.length) return circleDerivedOptions;
    if (!selectableOptions || !savedTimeSlots?.length) return undefined;
    const selected = getSelectedRepeatingOptionsFromSlots(
      selectableOptions,
      savedTimeSlots
    );
    return selected.length > 0 ? selected : undefined;
  }, [circleDerivedOptions, selectableOptions, savedTimeSlots]);

  // Mark the calendar_add action as completed when the user clicks any
  // add-to-calendar button. The hook's shouldUpdateStatus guard prevents
  // re-firing once the action is already Completed.
  const { mutate: markCalendarActionCompleted } = useUpdateActionStatus({
    actionId: calendarAddAction.id,
    status: ACTION_STATUS.COMPLETED,
    currentStatus: calendarAddAction.current_status?.name,
  });

  const handleAddCalendarEvent = useCallback(
    (
      _eventId: number | string | "all",
      _calendarType: "google" | "ical"
    ) => {
      markCalendarActionCompleted();
    },
    [markCalendarActionCompleted]
  );

  const handleToggleMeeting = useCallback(
    (meetingId: number) => {
      setHasUserToggled(true);
      setExpandedMeetingId((prev) => (prev === meetingId ? null : meetingId));
    },
    []
  );

  // Filter out past meetings (end_time before now)
  // When video is disabled, hide all meetings
  const visibleMeetings = useMemo(() => {
    if (isVideoDisabled || !meetings) return [];
    const now = new Date().toISOString();
    return meetings.filter((m) => !m.end_time || m.end_time >= now);
  }, [isVideoDisabled, meetings]);

  // Default-expand the current meeting until user manually toggles
  const effectiveExpandedId = hasUserToggled
    ? expandedMeetingId
    : currentMeeting?.id ?? null;

  // Determine what to render
  const hasMeetings = visibleMeetings.length > 0;
  const hasSelectedOptions =
    !meetingsOnly && selectedOptions && selectedOptions.length > 0;

  // Return null if nothing to display
  if (!hasMeetings && !hasSelectedOptions) {
    return null;
  }

  return (
    <ProgramCalendarEventList
      meetings={hasMeetings ? visibleMeetings : undefined}
      selectableOptions={hasSelectedOptions ? selectedOptions : undefined}
      eventTitle={eventTitle}
      eventDescription={eventDescription}
      userTimezone={userTimezoneOption ?? undefined}
      onAddCalendarEvent={handleAddCalendarEvent}
      currentMeetingId={currentMeeting?.id}
      expandedMeetingId={effectiveExpandedId}
      onToggleMeeting={handleToggleMeeting}
      actionsByMeetingId={actionsByMeetingId}
      meetingActionByMeetingId={meetingActionByMeetingId}
    />
  );
}

export default ProgramCalendarEventListContainer;
